class AudioManager {
    constructor() {
        this.context = new (window.AudioContext || window.webkitAudioContext)();
        this.store = {};
        // Create a gain node for volume control
        this.gainNode = this.context.createGain();
        this.gainNode.connect(this.context.destination);
        // Default volume: 5 out of 10 = 0.5 (50%)
        this.setVolume(5);
    }

    /**
     * Set the volume level (0-10 scale from options menu)
     * Uses a custom mapping for natural volume perception
     * @param {number} volume - Volume level from 0 to 10
     */
    setVolume(volume) {
        // Clamp volume to 0-10 range
        const clampedVolume = Math.max(0, Math.min(10, volume));

        // Custom volume mapping for each level
        const volumeMap = {
            0: 0.0,
            1: 0.01,
            2: 0.02,
            3: 0.04,
            4: 0.08,
            5: 0.12,
            6: 0.16,
            7: 0.2,
            8: 0.25,
            9: 0.35,
            10: 0.5,
        };

        const gainValue = volumeMap[clampedVolume] ?? 0;
        this.gainNode.gain.value = Math.max(0, Math.min(1, gainValue));
    }

    async get(path) {
        if (this.store[path]) {
            return this.store[path];
        }

        // console.warn(`Audio not loaded yet. Loading lazily: ${path}`);
        try {
            const buffer = await this.loadSingleAudio(path);
            return buffer;
        } catch (error) {
            console.error(`Failed to load audio at ${path}: ${error.message}`);
            throw error;
        }
    }

    async loadSingleAudio(path, maxRetries = 3, retryDelayMs = 200) {
        // Prepend assets/ to the path
        const fullPath = `assets/${path}`;

        let attempts = 0;
        while (attempts < maxRetries) {
            try {
                const response = await fetch(fullPath);
                if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await this.context.decodeAudioData(arrayBuffer);

                // Store using the original path as key, not the full path
                this.store[path] = audioBuffer;
                return audioBuffer;
            } catch {
                attempts++;
                console.warn(`Audio load attempt ${attempts} failed for ${fullPath}`);
                if (attempts >= maxRetries) {
                    throw new Error(`Failed to load audio after ${maxRetries} attempts: ${fullPath}`);
                }
                return await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
            }
        }
    }

    async fetchAudioPaths() {
        try {
            const response = await fetch('assets/audioPaths.json');
            if (!response.ok) throw new Error(`Failed to fetch paths: ${response.statusText}`);

            const data = await response.json();
            return data.audio_files;
        } catch (error) {
            console.error(`Error fetching audio paths: ${error.message}`);
            throw error;
        }
    }

    async loadAllAudios(maxRetries = 3, retryDelayMs = 1000) {
        const audioPaths = await this.fetchAudioPaths();

        let failedCount = 0;
        const total = audioPaths.length;

        for (let i = 0; i < audioPaths.length; i++) {
            const path = audioPaths[i];
            try {
                // Load each audio - this will store it in the cache
                await this.loadSingleAudio(path, maxRetries, retryDelayMs);
            } catch {
                failedCount++;
                console.warn(`Failed to load: ${path}`);
            }
        }

        const summary = `Audio loading complete.\nFailed: ${failedCount}\nSucceeded: ${total - failedCount}`;
        console.log(summary);

        return this.store;
    }

    play(path) {
        const audioBuffer = this.store[path];
        if (!audioBuffer) {
            console.warn(`Audio not loaded: ${path}`);
            return null;
        }

        const source = this.context.createBufferSource();
        source.buffer = audioBuffer;
        // Connect through gain node for volume control
        source.connect(this.gainNode);
        source.start(0);

        return source;
    }
}

export const audioManager = new AudioManager();
